import React, { useState } from "react";
import { useSelector } from "react-redux";
import PT from "prop-types";
import { styled } from "styled-components";
import { chain, find } from "lodash";
import { FormControl } from "@icg360/ui-toolkit";

import ClearFormControl from "../../toolkit/clear-form-control";
import { InputMeta } from "../renderers/input-meta";
import { hasFeatureFlag } from "../../../store/selectors";
import { MortgageeSearchDisplay } from "../../mortgagee-search/mortgagee-search-display";
import { MortgageeSearch } from "../../mortgagee-search/mortgagee-search";

/**
 * ---------------------------------------------------------------------------------
 * There are two functional components that Mortgagee Search is composed of:
 * 1. MortgageeSearch - the actual input field with dropdown
 * 2. MortgageeSearchDisplay - handles displaying mortgagee and clearing it
 * ---------------------------------------------------------------------------------
 */

// The fusionName value of all the inputs that are required for this fusion to work
const REQUIRED_INPUTS = [
  "company",
  "addressLine1",
  "addressLine2",
  "city",
  "state",
  "zip"
];

const mapObjectKeysWith = (object, mapper) =>
  chain(object)
    .toPairs()
    .map(([key, val]) => [mapper[key] || key, val])
    .fromPairs()
    .value();

const FusionError = styled.div`
  background-color: #f8d7da;
  padding: 30px;
  color: #842029;

  p {
    margin: 0;
    padding: 0;
  }
`;

export const QTCMortgageeSearch = ({
  inputs,
  onChange,
  passthroughProps,
  groupLabel
}) => {
  const [shouldFocus, setShouldFocus] = useState(false);
  const hasUpdatedMortgageesResponse = useSelector(
    hasFeatureFlag("updated-mortgagees-response")
  );

  const isHidden = inputs.every(
    ({ type, visible = true }) => type === "hidden" || !visible
  );
  if (isHidden) {
    return null;
  }

  const mortgagee = inputs.reduce((acc, { fusionName, value }) => {
    if (fusionName) {
      acc[fusionName] = value;
    }
    return acc;
  }, {});

  const hasValue = chain(mortgagee)
    .pick(
      "company",
      "clause1",
      "loanNumber",
      "addressLine1",
      "addressLine2",
      "city",
      "state",
      "zip"
    )
    .some()
    .value();

  /**
   * This mapping is necessary because the "keys" coming from product (fusionName)
   * are not the same as the keys coming from the backend api (where we get the listings for the Mortgagees search).
   * So this maps backend api keys to product fusion names.
   */
  const mortgageeApiKeyToFusionName = {
    Mortgagee0Name: "company",
    Mortgagee0AddressLine1: "addressLine1",
    Mortgagee0AddressLine2: "addressLine2",
    Mortgagee0City: "city",
    Mortgagee0State: "state",
    Mortgagee0ZipCode: "zip"
  };

  const updatedMortgageeApiKeyToFusionName = {
    Name: "company",
    AddressLine1: "addressLine1",
    AddressLine2: "addressLine2",
    City: "city",
    State: "state",
    ZipCode: "zip"
  };

  const handleChange = nextMortgagee => {
    const nextFusionMortgagee = nextMortgagee
      ? mapObjectKeysWith(
          nextMortgagee,
          hasUpdatedMortgageesResponse
            ? updatedMortgageeApiKeyToFusionName
            : mortgageeApiKeyToFusionName
        )
      : {
          company: "",
          clause1: "",
          loanNumber: "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          zip: ""
        };
    onChange(nextFusionMortgagee);
  };

  const validation = inputs.reduce(
    (
      { required, errors },
      { isRequired, validationErrors = [], label, value }
    ) => ({
      required: isRequired ? [...required, label] : required,
      errors: value
        ? [
            ...errors,
            ...validationErrors.map(error => ({
              ...error,
              message: `${label}: ${error.message}`
            }))
          ]
        : errors
    }),
    { required: [], errors: [] }
  );

  /** Fusion Inputs Validation */
  const inputErrors = checkRequiredFusionInputs(REQUIRED_INPUTS, inputs);
  if (inputErrors.length > 0) {
    return (
      <FusionError className="prose">
        <p>
          Product Error: Mortgagee Search fusion group does not contain required
          inputs:
        </p>
        <ul>
          {inputErrors.map(missingFusionName => (
            <li key={missingFusionName}>{missingFusionName}</li>
          ))}
        </ul>
      </FusionError>
    );
  }

  const DisplayComponent = MortgageeSearchDisplay;
  const SearchComponent = MortgageeSearch;

  return (
    <>
      <InputMeta
        className="QTCInput--mortgagee-search-fusion"
        ids={inputs.map(({ id }) => id).filter(i => i)}
        Control={hasValue && mortgagee ? DisplayComponent : SearchComponent}
        label={find(inputs, { fusionName: "company" })?.label}
        groupLabel={groupLabel}
        touched={hasValue}
        isRequired={validation.required.length > 0}
        passthroughProps={passthroughProps}
        controlProps={{
          fusionPosition: parseInt(
            find(inputs, { fusionName: "position" })?.value ||
              find(inputs, { fusionName: "search" })?.value
          ),
          onClear: () => setShouldFocus(true),
          shouldFocus
        }}
        onChange={handleChange}
        validationErrors={validation.errors}
        value={
          hasValue ? createMortgageeDisplayStringFromObject(mortgagee) : null
        }
      />
    </>
  );
};

QTCMortgageeSearch.propTypes = {
  onChange: PT.func.isRequired,
  inputs: PT.array.isRequired,
  passthroughProps: PT.object,
  groupLabel: PT.string.isRequired
};

const joinWith = (seperator, ...args) =>
  args.filter(value => value).join(seperator);

const createMortgageeDisplayStringFromObject = ({
  company,
  addressLine1,
  addressLine2,
  city,
  state,
  zip
}) =>
  joinWith(
    ", ",
    company,
    addressLine1,
    addressLine2,
    city,
    joinWith(" ", state, zip)
  );

// takes an array of input names, and the inputs array
// returns a boolean of whether or not it meets the expected inputs
const checkRequiredFusionInputs = (expectedInputs, inputs) =>
  expectedInputs.filter(fusionName => !find(inputs, { fusionName }));
